import { Assessment, Assignment, Group, ReportProblem, School, Search, Settings, SwapHoriz,
	VerifiedUser, Warning as WarningIcon } from "@mui/icons-material";
import { Button, FormControl, InputLabel, MenuItem, Select, Typography } from "@mui/material";
import { useAtom } from "jotai";
import { FC, Fragment, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { permissionsAtom } from "../../atoms/atom";
import AppBar from "../../components/app-bar/AppBar";
import EpiInfoBox from "../../components/epi/EpiInfoBox";
import EpiStatCard from "../../components/epi/EpiStatCard";
import { EPI_COLORS, companyLabel } from "../../components/epi/epiColors";
import { EpiInspectionService } from "../../services/EpiInspectionService";
import { EpiCompanyConfigDTO } from "../../types/EpiCompanyConfigDTO";
import { EpiPanelDTO } from "../../types/EpiPanelDTO";
import { useUtils } from "../../utils/utilFunctions";
import { getLoading } from "../execution-events/ExecutionEvents";
import './EpiInspectionPanel.scss';

const EpiInspectionPanel: FC = () => {
	const [permissions] = useAtom(permissionsAtom);
	const [companies, setCompanies] = useState<EpiCompanyConfigDTO[]>([]);
	const [companyId, setCompanyId] = useState<number | ''>('');
	const [panel, setPanel] = useState<EpiPanelDTO | null>(null);
	const [loading, setLoading] = useState(true);
	const [loadingPanel, setLoadingPanel] = useState(false);
	const navigate = useNavigate();
	const { verifyPermission } = useUtils();
	const service = new EpiInspectionService();

	useEffect(() => {
		const loadCompanies = async () => {
			const response = await service.getCompanies();
			const enabled = response?.data?.filter(item => item.enabled) || [];
			setCompanies(enabled);
			if (enabled.length) {
				setCompanyId(enabled[0].companyId);
			}
			setLoading(false);
		};
		loadCompanies();
		if (!!permissions.length) {
			verifyPermission(permissions, 'EPI_INSPECTION');
		}
	}, []);

	useEffect(() => {
		if (companyId === '') {
			setPanel(null);
			return;
		}
		const loadPanel = async () => {
			setLoadingPanel(true);
			const response = await service.getPanel(companyId);
			setPanel(response?.data || null);
			setLoadingPanel(false);
		};
		loadPanel();
	}, [companyId]);

	return (
		<Fragment>
			<AppBar
				modulo="Inspeção de EPIs"
			/>
			<div className="epi-inspection-panel">
				<div className="top">
					<Typography variant="h6">Painel Conformidade NR-06</Typography>
					<div className="panel-filters">
						<FormControl size="small" sx={{ width: { xs: '100%', md: '360px' } }}>
							<InputLabel id="epi-company-label">Empresa</InputLabel>
							<Select
								labelId="epi-company-label"
								label="Empresa"
								id="select-empresa"
								value={companyId}
								onChange={(e) => setCompanyId(Number(e.target.value))}
							>
								{companies.map(item => (
									<MenuItem key={item.companyId} value={item.companyId}>
										{companyLabel(item)}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						{permissions.includes('CREATE_EPI_INSPECTION') && (
							<Button
								variant="contained"
								startIcon={<Assignment />}
								onClick={() => navigate('/inspecao-epis/nova')}
							>
								Nova inspeção
							</Button>
						)}
						{!permissions.includes('MANAGE_EPI_COMPANIES')
							? <></>
							: <Button
								variant="contained"
								color="info"
								startIcon={<Settings />}
								onClick={() => navigate('/inspecao-epis/configuracoes')}
							>
								Configurações
							</Button>
						}
					</div>
				</div>
				{loading
					? getLoading()
					: !companies.length
						? <div className="sem-execucoes">
							<Typography variant="h6">
								Nenhuma empresa habilitada para inspeção de EPIs. Habilite em Configurações.
							</Typography>
						</div>
						: loadingPanel || !panel
							? getLoading()
							: <Fragment>
								<div className="cards">
									<EpiStatCard
										icon={<VerifiedUser />}
										color={EPI_COLORS.ok}
										value={formatIndex(panel.complianceIndex)}
										label="Índice de Conformidade NR-06"
										detail={`Meta: ${panel.complianceGoal}%`}
									/>
									<EpiStatCard
										icon={<Group />}
										color={EPI_COLORS.info}
										value={String(panel.monitoredEmployees)}
										label="Colaboradores monitorados"
										linkLabel={permissions.includes('EPI_INSPECTION') ? 'Ver colaboradores' : undefined}
										onLinkClick={() => navigate(`/inspecao-epis/detalhes/${companyId}/MONITORED`)}
									/>
									<EpiStatCard
										icon={<WarningIcon />}
										color={EPI_COLORS.warning}
										value={String(panel.openNonConformities)}
										label="Não conformidades abertas"
										linkLabel={permissions.includes('EPI_NONCONFORMITY') ? 'Ver não conformidades' : undefined}
										onLinkClick={() => navigate('/inspecao-epis/nao-conformidades')}
									/>
									<EpiStatCard
										icon={<SwapHoriz />}
										color={EPI_COLORS.purple}
										value={String(panel.pendingReplacements)}
										label="Substituições pendentes"
										linkLabel={permissions.includes('EPI_NONCONFORMITY') ? 'Ver substituições' : undefined}
										onLinkClick={() => navigate('/inspecao-epis/nao-conformidades')}
									/>
									<EpiStatCard
										icon={<ReportProblem />}
										color={EPI_COLORS.danger}
										value={String(panel.uninspectedEmployees)}
										label="Colaboradores não inspecionados no mês"
										linkLabel={permissions.includes('EPI_COMPLIANCE') ? 'Ver pendências' : undefined}
										onLinkClick={() => navigate('/inspecao-epis/conformidade')}
									/>
									<EpiStatCard
										icon={<School />}
										color={EPI_COLORS.orange}
										value={String(panel.pendingTrainings)}
										label="Orientações / Treinamentos pendentes"
										linkLabel={permissions.includes('EPI_INSPECTION') ? 'Ver orientações' : undefined}
										onLinkClick={() => navigate(`/inspecao-epis/detalhes/${companyId}/TRAININGS`)}
									/>
								</div>
								<div className="acoes-rapidas">
									<Typography variant="h6">Ações rápidas</Typography>
									<div className="cards-acoes">
										{permissions.includes('CREATE_EPI_INSPECTION') && (
											<EpiStatCard
												icon={<Assignment />}
												color={EPI_COLORS.info}
												value="Nova Inspeção"
												label="Realizar uma nova inspeção de EPIs em campo."
												linkLabel="Iniciar"
												onLinkClick={() => navigate('/inspecao-epis/nova')}
											/>
										)}
										{permissions.includes('EPI_INSPECTION') && (
											<EpiStatCard
												icon={<Search />}
												color={EPI_COLORS.purple}
												value="Acompanhamento"
												label="Consultar inspeções realizadas e situação dos colaboradores."
												linkLabel="Acessar"
												onLinkClick={() => navigate('/inspecao-epis/inspecoes')}
											/>
										)}
										{permissions.includes('EPI_NONCONFORMITY') && (
											<EpiStatCard
												icon={<ReportProblem />}
												color={EPI_COLORS.orange}
												value="Plano de Ação"
												label="Gerenciar não conformidades e ações corretivas."
												linkLabel="Acessar"
												onLinkClick={() => navigate('/inspecao-epis/nao-conformidades')}
											/>
										)}
										{permissions.includes('EPI_DASHBOARD') && (
											<EpiStatCard
												icon={<Assessment />}
												color={EPI_COLORS.success}
												value="Dashboard"
												label="Visualizar indicadores e estatísticas de conformidade."
												linkLabel="Acessar"
												onLinkClick={() => navigate('/inspecao-epis/dashboard')}
											/>
										)}
									</div>
								</div>
								<EpiInfoBox variant="info">
									<Typography variant="body2">
										Este painel apresenta dados consolidados sobre a conformidade com a NR-06.
									</Typography>
									<Typography variant="body2">
										Mantenha suas inspeções e registros atualizados para garantir um ambiente de
										trabalho seguro e em conformidade.
									</Typography>
								</EpiInfoBox>
							</Fragment>
				}
			</div>
		</Fragment>
	);

	function formatIndex(value?: number) {
		return value === undefined || value === null ? '—' : `${value.toFixed(1)}%`;
	}
};

export default EpiInspectionPanel;
